import React from 'react';
import Helmet from 'react-helmet';
import Layout from '~/layouts';
import Banners from '../components/banners';
import {
  getMarginBottomClass,
  getPaddingBottomClass,
  getPaddingTopClass,
  getBgColorClass,
  getBgIllustrationClass,
  fillVars,
  findHeroImage,
  cn,
  getSettingsClass,
  getMarginTopClass,
  getPageRichSnippets,
} from '../utils/helpers';
import { PAGE_TYPE } from '../utils/constants';
import { ImageLoadProvider } from '../utils/image-load-provider';
import { MenuStateProvider } from '~/utils/menu-state-provider';
import { ViewportProvider } from '~/utils/viewport-provider';

const SingleTemplate = props => {
  const { markdownRemark, config, headerMenu, mobileMenu } = props.data;

  const { slug } = props.pageContext;
  const { frontmatter } = markdownRemark;
  const { meta, header_color, sections, hides } = frontmatter;
  const { menu_src_override } = markdownRemark;
  const pageClass = slug === '/' ? 'index' : slug.replaceAll('/', '');
  const isFront =
    props.pageType !== PAGE_TYPE.NotFound &&
    (props.pageType === PAGE_TYPE.AcademyArticle || slug === '/');
  const menuBackgroundClass = `bg-menu--${header_color ? header_color : 'dark-blue'}`;
  let heroStarsClass = '';
  let customCssStyles = '';

  const richSnippets = getPageRichSnippets(meta);

  const siteVars = config.variables.reduce((acc, { id, value }) => {
    acc[id] = value;
    return acc;
  }, {});

  sections.forEach((section, sectionIndex) => {
    if (section.settings) {
      if (section.settings.custom_css) {
        customCssStyles = customCssStyles + ' ' + section.settings.custom_css;
      }
      // Split background (eg. Large card with half dark blue background)
      // Add class to previous section
      if (section.settings.negative_margin && sectionIndex > 0) {
        sections[sectionIndex - 1].settings.split_background = ' bg-split';
      }
    }

    section.blocks.forEach(block => {
      if (block.settings && block.settings.custom_css) {
        if (block.settings.custom_css.match(/undefined/) === null) {
          customCssStyles = customCssStyles + ' ' + block.settings.custom_css;
        }
      }
    });
  });

  // lcp improvement
  const heroImage = findHeroImage(frontmatter.sections[0]);

  return (
    <ViewportProvider>
      <ImageLoadProvider>
        <MenuStateProvider>
          <Layout
            external_scripts={config.external_scripts}
            global_meta_tags={config.global_meta_tags}
            header_color={header_color}
            hides={hides}
            inline_scripts={config.inline_scripts}
            location={props.location}
            menu_src_override={menu_src_override}
            site_name={props.seo.site_name}
            site_title={props.seo.title}
            top_banner={config.top_banner}
            header_menu={headerMenu}
            mobile_menu={mobileMenu}
          >
            <Helmet
              link={[{ rel: 'canonical', href: props.seo.canonical_url }]}
              meta={props.seo.socialz}
              title={props.seo.title}
              bodyAttributes={{
                class: `page--${pageClass} ${menuBackgroundClass} ${heroStarsClass}`,
              }}
            >
              {heroImage && (
                <link
                  rel='preload'
                  as='image'
                  href={heroImage}
                />
              )}
              {richSnippets && (
                <script type='application/ld+json'>
                  {richSnippets
                    .map(snippet => JSON.stringify(snippet))
                    .join('\n')}
                </script>
              )}
            </Helmet>
            <style>{customCssStyles}</style>
            <svg
              width='0'
              height='0'
              viewBox='0 0 1 1'
              xmlns='http://www.w3.org/2000/svg'
              preserveAspectRatio='xMidYMid'
            >
              <defs>
                <clipPath
                  id='centered-clip'
                  clipPathUnits='objectBoundingBox'
                >
                  <rect
                    x='0'
                    y='0'
                    width='1'
                    height='0.91'
                  />
                  <path d='M0.5 0.975C0.096 0.975 0 1 0 1V0.9H1V1C1 1 0.904 0.975 0.5 0.975Z' />
                </clipPath>
              </defs>
            </svg>
            <main className='main'>
              {sections.map((section, sectionIndex) => {
                const isHeroSection = section.blocks?.some(
                  block => block._template === 'block__hero'
                );
                return (
                  <Section
                    key={sectionIndex}
                    title={section.title}
                    has_shadow={section.has_shadow}
                    is_bottom_curved={section.is_bottom_curved}
                    background_width={section.background_width}
                    background_color={section.background_color}
                    background_illustration={section.background_illustration}
                    background_image={section.background_image}
                    reverse_gradient={section.reverse_gradient}
                    settings={section.settings}
                    section_margin={section.section_margin}
                    layout_settings={section.layout_settings}
                    rounded_corners={section.rounded_corners}
                    isHeroSection={isHeroSection}
                  >
                    <BlocksRenderer
                      component={props.proxy}
                      proxy={props.proxy}
                      pageClass={pageClass}
                      pageType={props.pageType}
                      parentPage={props.data.parentPage}
                      isFront={isFront}
                      siteVars={siteVars}
                      location={props.location}
                      article={props.data.article}
                      post={props.data.post}
                      posts={props.data.posts?.edges}
                      tags={props.data.tags?.edges}
                      featured={props.data.featured}
                      featuredCategories={props.data.featuredCategories?.edges}
                      glossaryItems={props.data.glossaryItems?.edges}
                      graphData={props.data.graphData}
                      pagePrefix={props.pagePrefix}
                    >
                      {section.blocks}
                    </BlocksRenderer>
                  </Section>
                );
              })}
            </main>
            <Banners
              fluffee_blacklist={config.fluffee_blacklist}
              hiring_banner={config.hiring_banner}
              cookie_banner={config.cookie_banner}
              privacy_banner={config.privacy_banner}
            />
          </Layout>
        </MenuStateProvider>
      </ImageLoadProvider>
    </ViewportProvider>
  );
};

const BlocksRenderer = ({
  children,
  component: Component,
  pageClass,
  pageType,
  parentPage,
  isFront,
  siteVars,
  location,
  article,
  post,
  posts,
  tags,
  featured,
  featuredCategories,
  glossaryItems,
  graphData,
  pagePrefix,
}) => {
  const { leftBlocks, rightBlocks, fullBlocks } = children.reduce(
    (acc, block, blockIndex) => {
      const blockWithVars = { ...block };
      // Custom CSS class names
      let cssClassName = '';
      if (block.settings) {
        if (block.settings.css_class_name) {
          cssClassName = ' ' + block.settings.css_class_name;
        }

        cssClassName += getMarginBottomClass(block.settings);
      }

      // Replace variables in text fields.
      for (const [key, value] of Object.entries(blockWithVars)) {
        blockWithVars[key] = fillVars(value, siteVars);
      }

      const columnBlock = (
        <Component
          key={blockIndex}
          block={{
            ...blockWithVars,
            css_class_name: cssClassName,
          }}
          pageClass={pageClass}
          pageType={pageType}
          parentPage={parentPage}
          isFront={isFront}
          siteVars={siteVars}
          cssClassName={cssClassName}
          location={location}
          article={article}
          post={post}
          posts={posts}
          tags={tags}
          featured={featured}
          featuredCategories={featuredCategories}
          glossaryItems={glossaryItems}
          graphData={graphData}
          pagePrefix={pagePrefix}
        />
      );
      if (block.column_select === 'Left') {
        acc.leftBlocks.push(columnBlock);
      } else if (block.column_select === 'Right') {
        acc.rightBlocks.push(columnBlock);
      } else {
        acc.fullBlocks.push(columnBlock);
      }

      return acc;
    },
    { leftBlocks: [], rightBlocks: [], fullBlocks: [] }
  );

  if (leftBlocks.length > 0 || rightBlocks.length > 0) {
    return (
      <>
        {fullBlocks ? fullBlocks : null}
        <div className='container'>
          <div className='row'>
            {leftBlocks && <div className='col-lg-8'>{leftBlocks}</div>}
            {rightBlocks && (
              <div className='col-lg-4 col--layout'>{rightBlocks}</div>
            )}
          </div>
        </div>
      </>
    );
  }

  return <>{fullBlocks}</>;
};

// This is a wrapper that houses some of the reusable style logic
const Section = ({
  children,
  title,
  has_shadow,
  is_bottom_curved,
  background_width,
  background_color,
  background_illustration,
  background_image,
  reverse_gradient,
  settings,
  section_margin,
  layout_settings,
  rounded_corners,
  isHeroSection,
}) => {
  const layoutSettings = layout_settings ? layout_settings[0] : {};
  let layoutClass = '';
  switch (layoutSettings?._template) {
    case 'boxed':
      layoutClass = 'layout--boxed';
      break;
    case 'fit_content':
      layoutClass = 'layout--content';
      break;
    default:
      layoutClass = 'layout--full';
  }
  const sectionBackground = getBgColorClass(background_color);
  const backgroundIllustration = getBgIllustrationClass(
    background_illustration
  );
  const isFullWidth = layoutClass === 'layout--full';
  const sectionPaddingTop = getPaddingTopClass(settings);
  const sectionPaddingBottom = getPaddingBottomClass(settings);
  const sectionPaddingClass = `${sectionPaddingTop} ${sectionPaddingBottom}`;
  const sectionMarginTop = getMarginTopClass(settings);
  const sectionMarginBottom = getMarginBottomClass(settings);
  // I noticed legacy code ignores margin on section settings
  const sectionMarginClass = isFullWidth
    ? ''
    : `${sectionMarginTop} ${sectionMarginBottom}`;

  const roundedClass = rounded_corners || '';

  let styleOverride = {};

  let splitBackground = '';
  if (settings?.split_background) {
    splitBackground = 'bg-split';
  }

  let splitSection = '';
  if (typeof section_margin === 'number' && section_margin !== 0) {
    // TinaCMS doesn't allow resetting numerical fields to no value.
    // We'll ignore the value if it's 0.
    splitSection = 'section--negative-margin';
    styleOverride.style = {
      marginTop: `${section_margin}rem`,
    };
  }

  let cssSectionClassName = '';
  if (settings?.css_class_name) {
    cssSectionClassName = settings.css_class_name;
  }
  if (reverse_gradient) {
    cssSectionClassName = cssSectionClassName + ' reverse-gradient';
  }
  if (background_image) {
    styleOverride.style = {
      backgroundImage: `url('${background_image}')`,
    };
  }

  const withWrapper =
    isHeroSection || has_shadow || !isFullWidth || is_bottom_curved;
  const Wrapper = withConditionalWrapper(SectionWrapper, withWrapper);

  const sectionClassName = cn(
    `section ${sectionBackground} ${backgroundIllustration} ${splitBackground} ${splitSection} ${layoutClass} ${sectionPaddingClass} ${sectionMarginClass} ${roundedClass} ${cssSectionClassName}`
  );

  return (
    <Wrapper layoutSettings={layoutSettings}>
      <CurvedElement shouldCurve={is_bottom_curved}>
        <section
          className={sectionClassName}
          {...styleOverride}
        >
          {children}
          <Shadow
            renderShadow={has_shadow}
            layoutClass={layoutClass}
          />
        </section>
      </CurvedElement>
    </Wrapper>
  );
};

const SectionWrapper = ({ layoutSettings, children }) => {
  const background = layoutSettings?.background_color || '';
  const settingsClass = getSettingsClass(layoutSettings);

  return (
    <section className={cn(`section-wrapper ${settingsClass} ${background}`)}>
      {children}
    </section>
  );
};

const Shadow = ({ renderShadow, layoutClass }) => {
  if (renderShadow) {
    return <div className={cn(`section-shadow ${layoutClass}`)} />;
  }
  return null;
};

const CurvedElement = ({ shouldCurve, children }) => {
  if (shouldCurve) {
    return <div className='curved-wrapper'>{children}</div>;
  }
  return children;
};

const withConditionalWrapper = (Component, useComponent = false) => {
  return ({ children, ...props }) => {
    if (useComponent) {
      return <Component {...props}>{children}</Component>;
    } else {
      return <React.Fragment>{children}</React.Fragment>;
    }
  };
};

export default SingleTemplate;
